import { useEffect, useMemo, useState } from 'react'
import { isEmpty } from 'lodash'
import { message } from 'antd'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import creatorService from '../../services/portal/creatorEc.service'
import {
    CreatorDetailHookType,
    CreatorEcType,
    CreatorType,
} from '../../type/CreatorType'
import { useAppDispatch, useAppSelector } from '../../store/hooks/AppHooks'
import artistCmsService from '../../services/artist/artistCms.service'
import groupCmsService from '../../services/groups/groupCms.service'
import artistService from '../../services/portal/artist.service'
import { ArtistActionTypes } from '../../store/constant/artist.const'
import { ArtistGroupActionType } from '../../store/constant/group.const'
import { DetailCategory } from '../../type/CategoryType'
import { ArtistType } from '../../type/ArtistType'
import { BLOG_CATEGORY_ID_QUERY } from '../../utils/constant'
import store from '../../store'
import { AppActionsType } from '../../store/constant/app.const'

// Slug and isArtist parameters only passed through when user access artist or group detail
function useCreatorDetail(
    slug: string | null = null,
    isArtist: boolean | null = null
): CreatorDetailHookType {
    const location = useLocation()
    const history = useHistory()
    const searchParams = new URLSearchParams(location.search)
    const qrBlogCategoryId = searchParams.get(BLOG_CATEGORY_ID_QUERY)
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const isDetailPage = slug && isArtist !== null
    const userProfile = useAppSelector((state) => state.user.me)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [isShow404Page, setIsShow404Page] = useState<boolean>(false)
    const [creatorEcInfo, setCreatorEcInfo] = useState<CreatorEcType | null>(
        null
    )
    const [currentLoginUser, setCurrentLoginUser] =
        useState<CreatorEcType | null>(null)
    const [creatorCms, setCreatorCms] = useState<CreatorType | null>(null)
    const [isArtistFlg, setIsArtistFlg] = useState<boolean>(isArtist ?? false)
    const [listArtist, setListArtist] = useState<ArtistType[]>([])
    const [selectedBlogCategoryId, setSelectedBlogCategoryId] =
        useState<number>(0)
    const groupsEc = useAppSelector((state) => state.artistGroup.groupsEc)
    const groupEcId = creatorCms?.ecId ? Number(creatorCms?.ecId) : null
    const groupDataFromEc = useMemo(() => {
        return (
            groupsEc.find((groupEc) => groupEcId && groupEc.id === groupEcId) ??
            null
        )
    }, [groupsEc, groupEcId])
    const creatorCategoryId = useMemo(() => {
        if (!slug) {
            const id = isArtistFlg
                ? creatorCms?.artist_id
                : groupDataFromEc?.groupCategoryId

            return id ?? null
        }

        if (isArtist) return creatorCms?.artist_id ?? null

        return groupDataFromEc?.groupCategoryId ?? 0
    }, [isArtist, creatorCms?.artist_id, groupDataFromEc, isArtistFlg])

    // Set blog categories
    useEffect(() => {
        let queryBlogCategoryId = Number(qrBlogCategoryId)
        if (!creatorCms) return

        if (creatorCms?.blog_categories?.length === 0) {
            if (queryBlogCategoryId) {
                searchParams.delete(BLOG_CATEGORY_ID_QUERY)
                history.push(`${location.pathname}?${searchParams.toString()}`)
            }

            return
        }

        if (queryBlogCategoryId) {
            // ID BlogCategoryID in params is not exist in creatorCms.blog_categories => remove it
            const isExistCategory = creatorCms?.blog_categories?.find(
                (category) =>
                    category.id === Number(queryBlogCategoryId) &&
                    creatorCms.isActiveBlogCategory === true
            )
            if (!isExistCategory) {
                queryBlogCategoryId = creatorCms.blog_categories[0].id

                searchParams.delete(BLOG_CATEGORY_ID_QUERY)
                history.push(`${location.pathname}?${searchParams.toString()}`)
            }
        }

        setSelectedBlogCategoryId(
            queryBlogCategoryId
                ? Number(queryBlogCategoryId)
                : creatorCms.blog_categories[0].id
        )
    }, [creatorCms, qrBlogCategoryId, searchParams, location, history])

    const [creatorCategory, setCreatorCategory] =
        useState<DetailCategory | null>(null)

    const loadData = async (creatorSlug: string, isCreatorArtist: boolean) => {
        if (isCreatorArtist) {
            const creatorArtist = await artistCmsService.getArtist(creatorSlug)
            dispatch({
                type: ArtistActionTypes.setArtistInfo,
                payload: creatorArtist,
            })
            setCreatorCms(creatorArtist)
        } else {
            const creatorGroup = await groupCmsService.getGroupBySlug(
                creatorSlug
            )
            dispatch({
                type: ArtistGroupActionType.setCurrentGroupData,
                payload: creatorGroup,
            })
            setCreatorCms(creatorGroup)
        }
    }

    useEffect(() => {
        setIsLoading(true)
        artistService
            .getList()
            .then((data) => {
                setListArtist(data)
            })
            .catch(() => {
                message.error(t('error.server_error'))
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [])

    useEffect(() => {
        if (isDetailPage) {
            setIsLoading(true)
            loadData(slug, isArtist)
            setIsLoading(false)
        }
    }, [slug, isArtist])

    useEffect(() => {
        if (isEmpty(userProfile)) return

        if (userProfile.pialaAuthToken) {
            setIsLoading(true)
            creatorService
                .getCreatorEcInfo(userProfile.pialaAuthToken)
                .then((response) => {
                    if (!slug) {
                        setCreatorEcInfo(response)
                    }
                    setCurrentLoginUser(response)
                })
                .catch(() => {
                    message.error(t('error.server_error'))
                })
                .finally(() => {
                    setIsLoading(false)
                })
        }
    }, [userProfile?.pialaAuthToken, slug])

    useEffect(() => {
        if (creatorEcInfo?.slug) {
            setIsLoading(true)
            setIsArtistFlg(creatorEcInfo.isArtist)

            if (creatorEcInfo.isArtist) {
                artistCmsService
                    .getArtist(creatorEcInfo.slug)
                    .then((response) => {
                        setCreatorCms(response)
                        dispatch({
                            type: ArtistActionTypes.setArtistInfo,
                            payload: response,
                        })
                    })
                    .catch(() => {
                        message.error(t('error.server_error'))
                    })
                    .finally(() => {
                        setIsLoading(false)
                    })
            } else {
                groupCmsService
                    .getGroupBySlug(creatorEcInfo.slug)
                    .then((response) => {
                        setCreatorCms(response)
                        dispatch({
                            type: ArtistGroupActionType.setCurrentGroupData,
                            payload: response,
                        })
                    })
                    .catch(() => {
                        message.error(t('error.server_error'))
                    })
                    .finally(() => {
                        setIsLoading(false)
                    })
            }
        }
    }, [creatorEcInfo?.isArtist, creatorEcInfo?.slug])

    useEffect(() => {
        if (creatorCategoryId) {
            setIsLoading(true)
            artistService
                .getDetailCategory(creatorCategoryId)
                .then((data: DetailCategory[]) => {
                    if (data?.length) {
                        setCreatorCategory(data[0])
                    }
                })
                .catch(() => {
                    message.error(t('error.server_error'))
                })
                .finally(() => {
                    setIsLoading(false)
                })
        }
    }, [creatorCategoryId])

    useEffect(() => {
        if (listArtist.length > 0 && creatorCms) {
            setIsShow404Page(false)

            if (isArtistFlg) {
                const artist = listArtist.find(
                    (item) =>
                        item.isGroup === 0 &&
                        Number(item.id) === creatorCms.artist_id
                )

                if (!artist) {
                    setIsShow404Page(true)
                }
            } else {
                const group = listArtist.find(
                    (item) =>
                        item.isGroup === 1 &&
                        Number(item.groupId) === creatorCms.id
                )

                if (!group) {
                    setIsShow404Page(true)
                }
            }
        }
    }, [listArtist, creatorCms])

    return {
        currentLoginUser,
        creatorEcInfo,
        isLoading,
        isShow404Page,
        creatorCms,
        isArtistFlg,
        creatorCategory,
        creatorCategoryId,
        listArtist,
        selectedBlogCategoryId,
        setSelectedBlogCategoryId,
    }
}

export default useCreatorDetail
